import { h } from './dom';

// Cloudflare's "always-passes" test sitekey — replace with your real one in
// .env (VITE_TURNSTILE_SITEKEY) once you've created a Turnstile site.
const TURNSTILE_SITEKEY = import.meta.env.VITE_TURNSTILE_SITEKEY || '1x00000000000000000000AA';

type Validator = (v: string) => boolean;

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const VALIDATORS: Record<string, Validator> = {
  name:   (v) => v.trim().length >= 1,
  email:  (v) => EMAIL_RX.test(v.trim()),
  budget: (v) => v.trim().length >= 1, // optional — empty stays neutral, filled shows ✓
  brief:  (v) => v.trim().length >= 1,
};

interface FieldDef { label: string; type?: string; placeholder: string; multiline?: boolean; name: string; required?: boolean; }

const FIELDS: FieldDef[] = [
  { label: '// name',   placeholder: 'who are you?',         name: 'name',   required: true },
  { label: '// email',  placeholder: 'you@company.com',      name: 'email',  type: 'email', required: true },
  { label: '// budget', placeholder: '$25k+ · or T&M',       name: 'budget' },
  { label: '// brief',  placeholder: 'what are you building?', name: 'brief', multiline: true, required: true },
];

function FieldRow(f: FieldDef): { row: HTMLElement; input: HTMLInputElement | HTMLTextAreaElement } {
  const attrs = {
    placeholder: f.placeholder,
    name: f.name,
    ...(f.required ? { required: 'required' } : {}),
  };
  const input = (f.multiline
    ? h('textarea', { rows: '2', ...attrs })
    : h('input', { type: f.type || 'text', ...attrs })) as HTMLInputElement | HTMLTextAreaElement;
  const row = h('div', { class: 's-contact__field' },
    h('label', {}, f.label),
    input,
  );

  const validator = VALIDATORS[f.name];
  if (validator) {
    const update = () => {
      const v = input.value;
      if (!v) row.dataset.validity = '';
      else row.dataset.validity = validator(v) ? 'valid' : 'invalid';
    };
    input.addEventListener('input', update);
    input.addEventListener('blur', () => {
      if (input.value || f.required) row.dataset.touched = '1';
      update();
    });
  }

  return { row, input };
}

type State = 'idle' | 'sending' | 'sent' | 'error';

const LABELS: Record<State, string> = {
  idle: 'SEND IT',
  sending: 'SENDING…',
  sent: '✓ MESSAGE QUEUED',
  error: '✗ FAILED',
};

const ERROR_MESSAGES: Record<string, string> = {
  'invalid json':                   'malformed request',
  'missing required fields':        'fill name, email & brief',
  'invalid email':                  'check the email format',
  'turnstile verification failed':  'captcha rejected — refresh and retry',
  'webhook not configured':         'server misconfigured',
  'webhook upstream failed':        'discord webhook unreachable',
};

export function Contact(): HTMLElement {
  const submitLabel = h('span', { class: 's-contact__submit-label' }, LABELS.idle);
  const submit = h('button', { type: 'submit', class: 's-contact__submit' },
    submitLabel,
    ' ',
    h('span', {}, '→'),
  );

  // honeypot — hidden, only bots fill it
  const honeypot = h('input', {
    type: 'text', name: 'hp', tabindex: '-1', autocomplete: 'off',
    'aria-hidden': 'true',
    style: 'position:absolute;left:-10000px;top:auto;width:1px;height:1px;overflow:hidden',
  });

  // Empty container — Turnstile widget is rendered explicitly once the form
  // is in the DOM (auto-render only fires for elements present at script load,
  // and our content mounts later).
  const turnstileEl = h('div', { class: 's-contact__turnstile' });
  let turnstileWidgetId: string | undefined;
  let turnstileToken = '';

  const mountTurnstile = () => {
    if (!turnstileEl.isConnected || !window.turnstile) {
      requestAnimationFrame(mountTurnstile);
      return;
    }
    turnstileWidgetId = window.turnstile.render(turnstileEl, {
      sitekey: TURNSTILE_SITEKEY,
      theme: 'dark',
      size: 'flexible',
      callback: (token) => { turnstileToken = token; },
      'expired-callback': () => { turnstileToken = ''; },
      'error-callback': () => { turnstileToken = ''; },
    });
  };
  requestAnimationFrame(mountTurnstile);

  const fields = FIELDS.map(FieldRow);

  const form = h('form', { class: 's-contact__form', novalidate: true },
    ...fields.map((f) => f.row),
    honeypot,
    turnstileEl,
    submit,
  );

  const setState = (s: State, detail?: string) => {
    const base = LABELS[s];
    submitLabel.textContent = detail ? `${base} — ${detail.toUpperCase()}` : base;
    submit.toggleAttribute('disabled', s === 'sending');
  };

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    // mark all required fields as "touched" so failing ones show their error
    fields.forEach(({ row, input }) => {
      const def = FIELDS.find((f) => f.name === input.name);
      if (def?.required) {
        row.dataset.touched = '1';
        row.dispatchEvent(new Event('input', { bubbles: true }));
        const validator = VALIDATORS[def.name];
        const v = input.value;
        if (validator) row.dataset.validity = !v ? 'invalid' : (validator(v) ? 'valid' : 'invalid');
      }
    });
    const data = new FormData(form as HTMLFormElement);
    const payload = {
      name: String(data.get('name') ?? ''),
      email: String(data.get('email') ?? ''),
      budget: String(data.get('budget') ?? ''),
      brief: String(data.get('brief') ?? ''),
      hp: String(data.get('hp') ?? ''),
      turnstile: turnstileToken,
    };

    if (!payload.turnstile) {
      setState('error', 'captcha not ready');
      setTimeout(() => setState('idle'), 4000);
      return;
    }

    setState('sending');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(10_000),
      });
      const data = await res.json().catch(() => ({})) as { error?: string };
      if (!res.ok) {
        const detail = data.error ? (ERROR_MESSAGES[data.error] ?? data.error) : `HTTP ${res.status}`;
        setState('error', detail);
        // tokens are single-use even on failure, so refresh the widget
        if (turnstileWidgetId && window.turnstile) {
          window.turnstile.reset(turnstileWidgetId);
          turnstileToken = '';
        }
        setTimeout(() => setState('idle'), 5000);
        return;
      }
      setState('sent');
      (form as HTMLFormElement).reset();
      if (turnstileWidgetId && window.turnstile) {
        window.turnstile.reset(turnstileWidgetId);
        turnstileToken = '';
      }
    } catch (err) {
      const detail = err instanceof Error && err.name === 'TimeoutError'
        ? 'timed out'
        : 'network error';
      setState('error', detail);
      if (turnstileWidgetId && window.turnstile) {
        window.turnstile.reset(turnstileWidgetId);
        turnstileToken = '';
      }
      setTimeout(() => setState('idle'), 5000);
    }
  });

  const caption = h('div', { class: 's-section__caption' });
  caption.append('// 04 CONTACT', h('br'), 'REPLIES < 48H');

  const title = h('h2', { class: 's-section__title' });
  title.append(h('b', {}, 'Got a brief?'), h('br'), 'Drop the shape of the problem.');

  const copyH2 = h('h2', {});
  copyH2.append('Let\'s', h('br'), 'build it', h('br'), h('span', {}, 'damn good.'));

  return h('section', { id: 'contact', class: 's-section' },
    caption,
    h('header', { class: 's-section__head' },
      h('span', { class: 's-section__num' }, '04'),
      title,
    ),
    h('div', { class: 's-contact' },
      h('div', { class: 's-contact__copy' },
        copyH2,
        h('p', {}, "Booked through Q3, but always reading. We'll get back inside 48 hours — usually faster."),
      ),
      form,
    ),
  );
}
